import React, { KeyboardEvent, useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom-v5-compat';
import { useLazyQuery } from '@apollo/client';
import debounce from 'lodash.debounce';

import { GET_SEARCH_RESULTS } from '@atom/graph/search';
import { ClickAwayListener, Icon, Progress, TextField } from '@atom/mui';
import colors from '@atom/styles/colors';
import layout from '@atom/styles/layout';
import { SearchInput, SearchResultsType } from '@atom/types/search';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import history from '@atom/utilities/history';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import SearchResults from './SearchResults';

import './searchBar.css';

const MIN_SEARCH_CHARS = 2;
const DEBOUNCE_TIME = 500;

const styles = {
  close: {
    cursor: 'pointer',
  },
  searchIcon: {
    paddingRight: '0.5rem',
  },
};

const SearchBar = () => {
  const [searchParams] = useSearchParams();

  const [focused, setFocused] = useState<boolean>(false);
  const [query, setQuery] = useState<string>('');
  const [results, setResults] = useState<SearchResultsType>();

  const [getSearch, { loading }] = useLazyQuery<
    { search: SearchResultsType },
    { input: SearchInput }
  >(GET_SEARCH_RESULTS, {
    fetchPolicy: 'no-cache',
    onCompleted: data => {
      setResults(data?.search);
    },
  });

  useEffect(() => {
    const isSearchPortal = !!window.location.pathname.includes('search');
    const queryParam = searchParams.get('query');

    if (isSearchPortal && isNilOrEmpty(query) && !isNilOrEmpty(queryParam)) {
      setQuery(queryParam);
    }
  }, [searchParams]);

  const fetchSearchOptionsDebounced = useCallback(
    debounce((value: string) => {
      getSearch({
        variables: {
          input: {
            query: value,
            top: true,
            showAdmin: hasRolePermissions(ROLE_SETS.ADMIN),
          },
        },
      });
    }, DEBOUNCE_TIME),
    [],
  );

  const handleQueryChange = (value: string = '') => {
    setQuery(value);

    if (!value) {
      fetchSearchOptionsDebounced.cancel();
    } else if (value.length >= MIN_SEARCH_CHARS) {
      fetchSearchOptionsDebounced(value);
    } else {
      fetchSearchOptionsDebounced.cancel();
    }
  };

  const onKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    const isSearchPortal = !!window.location.pathname.includes('search');

    if (isSearchPortal && event.key === 'Enter' && query) {
      history.push(`/search?query=${query}`);
    }
  };

  const handleClear = () => {
    setFocused(true);
    setResults(null);
    setQuery('');
  };

  const textFieldStyle = {
    padding: '0 0.5rem',
    borderRadius: '2px',
    backgroundColor: focused ? colors.neutral.white : colors.neutral.fog,
    boxShadow: focused ? layout.muiDropdownBoxShadow : 'none',
  };

  const inputStyle = {
    height: '2.5rem',
    color: focused ? colors.neutral.black : colors.neutral.dim,
  };

  const showResults = focused && query && !loading && results;

  return (
    <ClickAwayListener onClickAway={() => setFocused(false)}>
      <div styleName="search-bar-container">
        <TextField
          onChange={event => handleQueryChange(event.target.value)}
          onKeyDown={onKeyPress}
          value={query}
          fullWidth
          onFocus={() => setFocused(true)}
          placeholder="Search"
          style={textFieldStyle}
          disableUnderline
          InputProps={{
            style: inputStyle,
            startAdornment: <Icon style={styles.searchIcon}>search</Icon>,
            endAdornment: query && (
              <Icon style={styles.close} onClick={handleClear}>
                close
              </Icon>
            ),
          }}
        />
        <div styleName="results-popover">
          {loading && (
            <div styleName="loading-container">
              <Progress size={20} />
            </div>
          )}
          {showResults && (
            <SearchResults
              query={query}
              results={results}
              setFocused={setFocused}
            />
          )}
        </div>
      </div>
    </ClickAwayListener>
  );
};

export default SearchBar;
